import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import AiApi from '../../apis/AiApi';
import useApi from '../../lib/useApi';
import ChatBox from '../ChatBox/ChatBox';
import ActionCard from './components/ActionCard';
import CoursesCompleteInfoBlock from './components/CoursesCompleteInfoBlock';
import ImportantDates from './components/ImportantDates';
import LicenseCard from './components/LicenseCard';
import LicenseRenewalInfoBlock from './components/LicenseRenewalInfoBlock';
import MembersGridContent from './components/MembersGridContent';
import MgmtViewCard from './components/MgmtViewCard';
import YearlyLapseInfoBlock from './components/YearlyLapseInfoBlock';
import { DataRefreshProvider } from './lib/DataRefreshContext';
import ManagementGridProps from './lib/ManagementGridProps';

/**
 * The main view of the management dashboard
 */
const ManagementView = (props: ManagementGridProps) => {
  const aiApi = useApi(AiApi);
  const [isLoading, setIsLoading] = useState(true);
  const [initialMessage, setInitialMessage] = useState('Hi!👋 How can I help?');
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  useEffect(() => {
    if (props.orgId) return;
    setIsLoading(true);
    aiApi
      .GetInitialManagerResponse()
      .then(setInitialMessage)
      .catch(() => {
        /* Intentionally ignoring */
      })
      .finally(() => setIsLoading(false));
  }, [aiApi, props.orgId]);

  return (
    <DataRefreshProvider>
      <Box width="100%" position="relative">
        <Grid container spacing={2} padding={2} sx={{ overflow: 'auto' }}>
          <Grid
            item
            container
            lg={props.orgId ? 12 : 8}
            xs={12}
            rowSpacing={2}
            columnSpacing={2}
          >
            <Grid item xs={12}>
              <MgmtViewCard>
                <ActionCard {...props} />
              </MgmtViewCard>
            </Grid>
            <Grid
              item
              md={4.5}
              sm={0}
              xs={0}
              sx={{ display: isMd ? 'initial' : 'none' }}
            >
              <MgmtViewCard>
                <ImportantDates {...props} />
              </MgmtViewCard>
            </Grid>
            <Grid item md={4.5} sm={6} xs={12}>
              <MgmtViewCard>
                <CoursesCompleteInfoBlock {...props} />
              </MgmtViewCard>
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
              <MgmtViewCard>
                <LicenseCard {...props} />
              </MgmtViewCard>
            </Grid>
            <Grid
              item
              md={0}
              sm={6}
              xs={12}
              sx={{ display: isMd ? 'none' : 'initial' }}
            >
              <MgmtViewCard>
                <LicenseRenewalInfoBlock />
              </MgmtViewCard>
            </Grid>
            <Grid
              item
              md={0}
              sm={6}
              xs={12}
              sx={{ display: isMd ? 'none' : 'initial' }}
            >
              <MgmtViewCard>
                <YearlyLapseInfoBlock />
              </MgmtViewCard>
            </Grid>
          </Grid>
          {props.orgId == undefined && (
            <Grid item lg={4} xs={12} sx={{ height: '500px' }}>
              <ChatBox
                isLoading={isLoading}
                initialMessage={initialMessage}
                converse={async (m) => {
                  const reply = await aiApi.Converse({ message: m });
                  return reply.message;
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <MgmtViewCard>
              <MembersGridContent {...props} />
            </MgmtViewCard>
          </Grid>
        </Grid>
      </Box>
    </DataRefreshProvider>
  );
};

export default ManagementView;
